/**
 * Public API contracts (/api/public/v1/*).
 *
 * Deliberately SEPARATE from records.ts. Internal SPA responses may change
 * freely; anything in this file is a promise to external integrators and may
 * only change via /api/public/v2.
 */

import { z } from 'zod';
import { MAX_CHAT_UPLOAD_BYTES } from './records';

/**
 * A UTC ISO-8601 datetime, seconds optional — what the public contract has
 * accepted since v1. zod 4.5 made `.datetime()` require seconds (RFC 3339);
 * an integrator sending `2026-01-01T10:00Z` must not start getting a 400 from
 * a dependency bump (#2290).
 *
 * The 400 must read as it did, too: `publicValidationError` puts the first
 * issue's message in `details.issue`. A bare union reports "Invalid input",
 * so the union carries the old "Invalid ISO datetime", and `z.string()` in
 * front keeps "expected string, received number" for a wrong type.
 *
 * One instance: `.optional()` and `.refine()` wrap it without mutating, and
 * every schema built at module load is startup CPU (#2290).
 */
const publicDateTime = z.string().pipe(
  z.union([z.string().datetime(), z.string().datetime({ precision: -1 })], {
    error: 'Invalid ISO datetime',
  })
);

// ============================================================================
// Scopes
// ============================================================================

export const apiKeyScopeSchema = z.enum(['records:write', 'records:read']);

// ============================================================================
// Upload
// ============================================================================

export const publicCallDirectionSchema = z.enum(['inbound', 'outbound']);
export const publicRecordTypeSchema = z.enum(['call', 'chat']);
export const publicCrmEntityTypeSchema = z.enum(['deal', 'lead', 'contact', 'company']);

/**
 * Ceiling for a single call/chat's stated duration: 24 hours.
 *
 * Not chosen to match any real call — no legitimate session comes close, so
 * this costs a genuine caller nothing. It exists to catch a malformed value
 * (a units mixup, a decade-long "call" from a broken export) before it enters
 * leaderboard totals: those are sums over `durationSeconds`, and one corrupt
 * row skews the aggregate by orders of magnitude, not by an outlier the
 * average absorbs.
 */
const MAX_RECORD_DURATION_SECONDS = 24 * 60 * 60;

/**
 * Plausible range for `occurredAt`, the caller-supplied business time of a
 * call.
 *
 * Floor 2000-01-01: nothing about the product predates it, and it sits well
 * before any genuine backfill an integrator could hold — its job is to catch
 * a units mistake (epoch-seconds handed to a millisecond parser lands in
 * 1970) rather than to constrain real history.
 *
 * Ceiling now + 24h, evaluated inside `.refine()` (parse time, not module
 * load) so it moves with real time. `occurredAt` describes something that
 * already happened, so the 24h margin is clock-skew tolerance between the
 * caller and us, not a "scheduled for later" allowance — the field has no
 * such meaning.
 *
 * Both ends exist for the same reason as the duration cap above: dashboards
 * and funnels bucket by this timestamp, and a caller-supplied 1970 or 2200
 * corrupts them exactly as badly as a decade-long duration corrupts a sum.
 */
const OCCURRED_AT_MIN = new Date('2000-01-01T00:00:00.000Z');
const OCCURRED_AT_FUTURE_SKEW_MS = 24 * 60 * 60 * 1000;

export const publicUploadRequestSchema = z
  .object({
    // `.trim()` before `.min(1)`, so the check sees the trimmed value: plain
    // `.min(1)` passes `"   "`, and a whitespace-only projectId then reaches
    // the lookup verbatim and comes back as a 404 that blames the project
    // rather than a 400 that names the field. No body format makes `"   "` a
    // deliberate value, so this holds for JSON as much as for a form.
    projectId: z.string().trim().min(1),
    type: publicRecordTypeSchema,
    externalId: z.string().min(1).optional(),
    /** Business time of the call. Stored, but v1 dashboards bucket by ingest time. */
    occurredAt: publicDateTime
      .refine(
        (value) => {
          const ts = new Date(value).getTime();
          return (
            ts >= OCCURRED_AT_MIN.getTime() &&
            ts <= Date.now() + OCCURRED_AT_FUTURE_SKEW_MS
          );
        },
        { message: `occurredAt must be between ${OCCURRED_AT_MIN.toISOString()} and now` }
      )
      .optional(),
    fileUrl: z
      .string()
      .url()
      .refine((u) => u.startsWith('https://'), {
        message: 'fileUrl must be https',
      })
      .optional(),
    /**
     * Chat transcript text (type: 'chat'). Capped at MAX_CHAT_UPLOAD_BYTES —
     * the same ceiling the multipart chat upload enforces on the file part
     * (POST /records/upload) — so inlining the transcript as JSON isn't a way
     * around that cap. The route then holds the stored text itself to
     * MAX_STORED_CHAT_TEXT_BYTES in real UTF-8 bytes (413), and a chat over
     * MAX_ANALYZED_CHAT_BYTES is stored but not analysed (`chat_not_analyzed`
     * warning, #1489).
     *
     * `.max()` counts code points (zod ≥ 4.5), not bytes, so for multi-byte
     * text this is a looser bound than the number implies — up to four UTF-8
     * bytes per code point. That is acceptable
     * here: the real memory guard is requireContentLengthWithin() on the
     * route, which measures the whole request in real bytes before the body
     * is ever parsed — this check is schema-level defense in depth, not the
     * primary one.
     */
    textContent: z.string().max(MAX_CHAT_UPLOAD_BYTES).optional(),
    /** Free-form: CRM ids are numeric strings, telephony ids are operator ids. */
    agentId: z.string().min(1).optional(),
    // `.min(1)` throughout: an empty string is not a name, and storing '' where
    // the column means NULL gives every reader a second kind of "absent" to
    // handle. Rejecting says so at the boundary instead.
    agentName: z.string().min(1).optional(),
    participantName: z.string().min(1).optional(),
    participantPhone: z.string().min(1).optional(),
    agentPhone: z.string().min(1).optional(),
    // Дробь принимается НАМЕРЕННО: провайдеры телефонии отдают длительность
    // с долями секунды, и отвергать 65.4 значило бы заставлять клиента
    // округлять за нас. К целому её приводит граница записи
    // (`normalizeDurationWrite` в RecordMetadataBaseRepository) — колонка
    // объявлена целочисленной, а схема ответа дашборда требует `.int()`.
    // Не добавляй сюда `.int()`: это ломающее изменение публичного API.
    durationSeconds: z.number().min(0).max(MAX_RECORD_DURATION_SECONDS).optional(),
    callDirection: publicCallDirectionSchema.optional(),
    language: z.string().min(2).max(8).optional(),
    crmEntityType: publicCrmEntityTypeSchema.optional(),
    crmEntityId: z.string().min(1).optional(),
    crmEntityName: z.string().min(1).optional(),
    crmActivityName: z.string().min(1).optional(),
  })
  .strict();

/**
 * Multipart counterpart of the JSON body, for POST /records/upload.
 *
 * Same analytics fields, minus the two that describe where the content comes
 * from: the audio arrives as the `file` part, and a chat transcript has no
 * file to upload at all (use POST /records with textContent).
 *
 * Every value in a multipart body is a string, hence the coercion on
 * durationSeconds — the JSON schema takes a real number there and must keep
 * doing so, so this cannot simply reuse it.
 */
export const publicMultipartUploadBaseSchema = publicUploadRequestSchema
  .omit({ fileUrl: true, textContent: true, type: true, durationSeconds: true })
  .extend({
    /** Defaults to 'call': a multipart upload carries audio by definition. */
    type: publicRecordTypeSchema.default('call'),
    /** Coerced because a form can only send text. */
    durationSeconds: z.coerce.number().min(0).max(MAX_RECORD_DURATION_SECONDS).optional(),
  });

/**
 * In a multipart body, an empty OPTIONAL field means "not sent".
 *
 * The shape a form arrives in is `curl -F "crmEntityName=$NAME"`, and an unset
 * shell variable sends `""` rather than omitting the part. Failing the whole
 * upload over a blank *optional* field is a bad trade on a path that exists for
 * convenience — and the warnings already tell the caller what didn't make it.
 *
 * It also removes a silent corruption: `z.coerce.number()` turns `''` into `0`,
 * so a blank `durationSeconds` used to store a zero-second call AND suppress
 * its own `missing_duration` warning (`collectWarnings` tests for `undefined`,
 * and 0 is defined).
 *
 * Applied by walking the shape and asking each field whether it accepts
 * absence, rather than by a hand-written field list: a field added to the
 * contract later inherits the rule instead of quietly becoming the one
 * exception.
 *
 * Required fields get the mirror treatment — blank collapses to `''`, which the
 * field's own checks then reject. Collapsing to `''` rather than letting the
 * blank through matters for any required field that does not trim for itself:
 * `.min(1)` counts `"   "` as three characters and would accept it. Today
 * `projectId` does trim, so the two are indistinguishable on this contract —
 * but the rule is stated for every required field, not for the ones that
 * happen to trim, and the next one added would silently store whitespace.
 * Collapsing to `''` rather than to `undefined` is what makes the caller's
 * error say the value was empty instead of missing, which for `-F "field="`
 * is the difference between a usable message and a misleading one.
 *
 * Blank optional means absent; blank required means error.
 *
 * JSON bodies keep plain `.min(1)`: there, `""` is a deliberate value a caller
 * typed, not an artifact of string interpolation.
 */
function normalizeBlank(field: z.ZodType): z.ZodType {
  const isBlank = (value: unknown) => typeof value === 'string' && value.trim() === '';
  // `safeParse(undefined)` rather than `isOptional()`: the two are the same
  // test — that is what isOptional does internally — but isOptional is
  // deprecated and goes away in a Zod major.
  const acceptsAbsence = field.safeParse(undefined).success;
  return z.preprocess(
    (value) => (isBlank(value) ? (acceptsAbsence ? undefined : '') : value),
    field
  );
}

/**
 * The blank rule over a whole object shape.
 *
 * Exported so the rule can be exercised on a shape built for the purpose —
 * a required field that does NOT trim for itself, which this contract does not
 * currently contain. Testing it only through the real schema would pin the rule
 * to a property of today's fields rather than to the rule.
 */
export function applyBlankRule(
  shape: Record<string, z.ZodType>
): Record<string, z.ZodType> {
  return Object.fromEntries(
    Object.entries(shape).map(([name, field]) => [name, normalizeBlank(field)])
  );
}

export const publicMultipartUploadFieldsSchema = z
  .object(applyBlankRule(publicMultipartUploadBaseSchema.shape))
  // The preprocess only maps `''` to `undefined`, so the parsed OUTPUT is
  // identical to the base schema's — only the accepted input widens. Restating
  // the base type keeps callers' destructuring typed, which the mapped-shape
  // construction above would otherwise erase to Record<string, unknown>.
  .strict() as unknown as typeof publicMultipartUploadBaseSchema;

// ============================================================================
// Queries
// ============================================================================

/** Above this the caller is paging too greedily; the request is capped, not rejected. */
export const PUBLIC_RECORD_LIST_MAX_LIMIT = 100;

export const publicRecordListQuerySchema = z
  .object({
    // Capped rather than `.max()`-rejected: an integrator asking for too much
    // should be slowed down, not broken. A limit that isn't a number at all is
    // a different thing — that is a typo, and it still fails.
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .default(50)
      .transform((v) => Math.min(v, PUBLIC_RECORD_LIST_MAX_LIMIT)),
    offset: z.coerce.number().int().min(0).default(0),
    /** Inclusive lower/upper bounds on ingest time (ISO-8601). */
    createdAfter: publicDateTime.optional(),
    createdBefore: publicDateTime.optional(),
  })
  .strict();

// ============================================================================
// Responses
// ============================================================================

/** Non-fatal notices: the record was created but will miss some analytics. */
export const publicUploadWarningSchema = z.object({
  code: z.enum([
    'missing_agent_id',
    'missing_crm_entity',
    'missing_duration',
    // #1489: the chat is over MAX_ANALYZED_CHAT_BYTES — stored, not analysed.
    'chat_not_analyzed',
  ]),
  message: z.string(),
});

export const publicRecordResponseSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  type: publicRecordTypeSchema,
  status: z.string(),
  externalId: z.string().nullable(),
  /** Nullable to match the column: records_metadata.created_at carries a SQL
   * default rather than NOT NULL, so a row can type as absent. Every ingest
   * path sets it explicitly, so in practice this is always a timestamp. */
  createdAt: z.string().nullable(),
  occurredAt: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  callDirection: publicCallDirectionSchema.nullable(),
  warnings: z.array(publicUploadWarningSchema).optional(),
});

/**
 * The scored components of an analysis, all on the 0-100 scale.
 *
 * Null means "this analysis produced no value for it" — a scoreless call
 * category, or a rubric item the project switched off. The stored metrics
 * object omits absent keys entirely; an external contract should not make an
 * integrator tell an absent key from a zero.
 *
 * Deliberately NOT the whole stored metrics object: `crmScore` lives there too,
 * but it was removed from the quality rubric (it measures CRM hygiene, not
 * conversation quality), and publishing it here would re-freeze a metric the
 * product has already retired.
 */
export const publicAnalysisMetricsSchema = z.object({
  greetingScore: z.number().nullable(),
  needsScore: z.number().nullable(),
  clarityScore: z.number().nullable(),
  objectionScore: z.number().nullable(),
  closingScore: z.number().nullable(),
  politenessScore: z.number().nullable(),
  /** Predicted sale probability, 0-100. Calls only; null for chats and scoreless categories. */
  saleProbability: z.number().nullable(),
});

/**
 * Narrower than the stored analysis on purpose: the provider, model, prompt
 * profile, token counts and cost are ours, not the caller's, and the raw LLM
 * envelope (`result_json`) is an internal shape that changes with every rubric
 * revision — freezing it here would make every prompt change a breaking API
 * change.
 */
export const publicAnalysisResponseSchema = z.object({
  recordId: z.string(),
  analysisId: z.string(),
  createdAt: z.string().nullable(),
  summary: z.string().nullable(),
  /** Mean of whichever component scores below are present, 0-100, weighted by
   * the record's project weights (#874; equal weights — the plain mean). Null
   * when the analysis produced none, or when every present component weighs 0.
   * This is the same canonical figure the dashboards show — not the model's own
   * self-reported aggregate. */
  qualityScore: z.number().nullable(),
  metrics: publicAnalysisMetricsSchema,
});

export const publicRecordListResponseSchema = z.object({
  data: z.array(publicRecordResponseSchema),
  pagination: z.object({
    limit: z.number(),
    offset: z.number(),
    total: z.number(),
  }),
});

// ============================================================================
// Errors
// ============================================================================

/**
 * The only machine-readable field of the public error contract — integrators
 * branch on it, so an undocumented code lands in their `default` branch and
 * changes their retry behaviour silently.
 *
 * Adding a value here therefore also requires a row in the error table of
 * `apps/website/src/content/docs/<locale>/docs/api/index.md`, in all four
 * locales. `npm run check:public-error-codes` derives the required set from
 * this enum and fails CI until they match.
 */
export const publicErrorCodeSchema = z.enum([
  'invalid_api_key',
  'insufficient_scope',
  'tier_not_eligible',
  /** The organization's own administrator has switched the public API off
   * (feature flag `enablePublicApi`). Deliberately distinct from
   * `tier_not_eligible`: the plan is fine and upgrading changes nothing, so
   * the caller's move is to talk to their administrator, not to billing. The
   * key itself is still valid — hence not `invalid_api_key` either. */
  'feature_disabled',
  'project_not_found',
  /** The project exists and belongs to the caller's org, but its status is
   * `paused` or `archived`. Distinct from `project_not_found`, which
   * deliberately HIDES whether the project exists ("fix your id" — see the
   * comment on `project_not_found` at its call sites); this code confirms
   * the project is real and names the reason ("turn it back on"), so the
   * caller's fix is the opposite one. Also distinct from `feature_disabled`,
   * which is an org-wide switch — this is a single project's own status. */
  'project_not_accepting_records',
  'record_not_found',
  /** The record is visible, but carries no analysis yet. Distinct from
   * `record_not_found` because the caller's next move is the opposite one:
   * keep polling rather than fix the id. The record's current status travels
   * in `details` so they can tell "still processing" from "will never be
   * analyzed". */
  'analysis_not_found',
  /** The request reached the public namespace but matched no route. Distinct
   * from the *_not_found codes above, which mean "the endpoint exists, the
   * resource does not". */
  'not_found',
  'insufficient_balance',
  'rate_limited',
  'payload_too_large',
  'unsupported_media_type',
  'invalid_request',
  'fetch_failed',
  /** The audio URL's hostname couldn't be resolved/verified (DNS resolver
   * error, timeout, or malformed response) — our infrastructure, not a bad
   * URL. Answered 503 with Retry-After (see PUBLIC_ERROR_STATUS below).
   * Distinct from `fetch_failed` (source URL itself failed) and
   * `invalid_request`-style rejections (URL is disallowed on purpose). */
  'resolution_unavailable',
  /** A backing store we depend on (currently Postgres, which holds analyses)
   * could not be reached. Ours, not the caller's — answered 503 with
   * Retry-After. Distinct from `resolution_unavailable`, which is about the
   * caller's own audio URL failing to resolve. */
  'service_unavailable',
  'internal_error',
]);

/**
 * Statuses the public surface can produce. Deliberately a closed union: a
 * route reaching for a status that isn't here is a contract decision, not a
 * typo to be waved through.
 */
export type PublicErrorStatus =
  | 400
  | 401
  // 402 carries insufficient_balance: the org is authenticated and entitled,
  // it simply cannot pay for this call. Distinct from 403 (tier_not_eligible /
  // feature_disabled), which is about permission rather than funds.
  | 402
  | 403
  | 404
  // Added for `project_not_accepting_records`: the request is well-formed,
  // authenticated and points at a real, owned project — it conflicts with
  // that project's current (paused/archived) status, which is exactly what
  // 409 means and no other status here covers. This is a contract decision,
  // not a typo: extending this union is what forces `PUBLIC_ERROR_STATUS`
  // below to be updated deliberately for any code that wants it, rather than
  // silently compiling against a status the published docs don't expect.
  | 409
  | 413
  | 415
  | 429
  | 500
  | 503;

/**
 * The HTTP status each error code is answered with. One entry per code, and
 * the only place the pairing is decided.
 *
 * Lives beside the enum rather than in edge-api because it is contract, not
 * implementation: the published reference tables carry a `Status` column in
 * all four locales, and `scripts/check-public-error-codes.mjs` checks them
 * against this object. A guard reaching into `apps/` for contract data would
 * be the dependency pointing the wrong way — and would drag Hono into a plain
 * Node script for no reason.
 *
 * Before this existed the status was a call argument repeated at 54 sites.
 * They happened to agree — every code carried exactly one status — but nothing
 * held them there, and the docs had nothing to be checked against: a route
 * could answer `record_not_found` with a 400 and no test, type, or guard would
 * notice.
 *
 * `Record<PublicErrorCode, …>` is the point of the annotation, not decoration:
 * adding a code to `publicErrorCodeSchema` without deciding its status stops
 * compiling here.
 */
export const PUBLIC_ERROR_STATUS: Record<PublicErrorCode, PublicErrorStatus> = {
  invalid_api_key: 401,
  insufficient_scope: 403,
  tier_not_eligible: 403,
  feature_disabled: 403,
  project_not_found: 404,
  // The project is real and owned by the caller — conflicts with its own
  // current status, not "missing". See the note on the enum member above.
  project_not_accepting_records: 409,
  record_not_found: 404,
  analysis_not_found: 404,
  not_found: 404,
  // Authenticated and entitled, simply out of funds — see the note on 402 above.
  insufficient_balance: 402,
  rate_limited: 429,
  payload_too_large: 413,
  unsupported_media_type: 415,
  invalid_request: 400,
  // The caller's own source URL failed: their problem to fix, not retryable
  // by us. Distinct from resolution_unavailable directly below.
  fetch_failed: 400,
  // Our DNS resolver, not their URL — retryable, and paired with Retry-After
  // at the call sites that emit it.
  resolution_unavailable: 503,
  service_unavailable: 503,
  internal_error: 500,
};

export const publicErrorSchema = z.object({
  error: z.object({
    code: publicErrorCodeSchema,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

// ============================================================================
// Key management (internal, session-authenticated)
// ============================================================================

/**
 * Ceiling on ACTIVE keys per organization. Lives here rather than in the route
 * because the settings UI has to know it too — it disables the create control
 * and explains why, instead of letting the user fill in a form that the server
 * will answer with a 422. Two copies of the number would drift the moment
 * either side changed it.
 */
export const MAX_ACTIVE_API_KEYS_PER_ORG = 10;

export const createApiKeyRequestSchema = z
  .object({
    name: z.string().min(1).max(64),
    scopes: z.array(apiKeyScopeSchema).min(1),
    expiresAt: publicDateTime.optional(),
  })
  .strict();

export const apiKeySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  keyPrefix: z.string(),
  scopes: z.array(apiKeyScopeSchema),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  // Computed server-side from the same isApiKeyActive rule the auth gate
  // uses (not revoked AND not expired) — @aica/database, where that rule
  // lives, isn't importable from ui-worker, so the UI must not re-derive
  // activity from revokedAt/expiresAt itself.
  isActive: z.boolean(),
});

/** Only ever returned once, at creation. */
export const createApiKeyResponseSchema = z.object({
  key: apiKeySummarySchema,
  plaintext: z.string(),
});

/** GET /settings/api-keys. Summaries only — no plaintext, no hash. */
export const apiKeyListResponseSchema = z.object({
  data: z.array(apiKeySummarySchema),
});

// ============================================================================
// Inferred types
// ============================================================================

export type ApiKeyScope = z.infer<typeof apiKeyScopeSchema>;
export type PublicUploadRequest = z.infer<typeof publicUploadRequestSchema>;
export type PublicMultipartUploadFields = z.infer<
  typeof publicMultipartUploadFieldsSchema
>;
export type PublicRecordListQuery = z.infer<typeof publicRecordListQuerySchema>;
export type PublicRecordResponse = z.infer<typeof publicRecordResponseSchema>;
export type PublicRecordListResponse = z.infer<typeof publicRecordListResponseSchema>;
export type PublicAnalysisResponse = z.infer<typeof publicAnalysisResponseSchema>;
export type PublicUploadWarning = z.infer<typeof publicUploadWarningSchema>;
export type PublicErrorCode = z.infer<typeof publicErrorCodeSchema>;
export type CreateApiKeyRequest = z.infer<typeof createApiKeyRequestSchema>;
export type ApiKeySummary = z.infer<typeof apiKeySummarySchema>;
export type CreateApiKeyResponse = z.infer<typeof createApiKeyResponseSchema>;
export type ApiKeyListResponse = z.infer<typeof apiKeyListResponseSchema>;
